import { makeObservable, observable, action, computed, reaction } from 'mobx';


//
// Implements navigation algorithms + state of current questionnaire session.
//
export class Session {
    sectionList = [];

    currentQuestion = null;
    nextNavigation = "";
    loading = true;

    historyStack = [];

    // by default, answer all questions one by one
    answeringMode = 'all';

    constructor(sectionList, panel) {
        this.sectionList = sectionList;
        this.panelId = panel.id;
        makeObservable(this, {
            // Component States
            loading: observable,
            answeringMode: observable,
            setLoading: action,
            setAnsweringMode: action,
            // Main display
            sectionList: observable,
            currentQuestion: observable,
            setAsCurrent: action,

            setupNavigation: action,
            setFirstQuestion: action,

            nextNavigation: observable,
            setNextNavigation: action,
            //
            startOver: action,
            startUnanswered: action,
            startIDontKnow: action,

            //
            // Allowing user to navigate back
            historyStack: observable,
            pushHistory: action,
            goBack: action,
            clearHistory: action,

            //
            // statistic
            countAll: computed,
            countVisible: computed,
            countAnswered: computed,
            countIDontKnow: computed,
        });

        reaction(() => this.sectionList.preloading, (preloading) => {
            if (!preloading) {
                this.setupNavigation();
                setTimeout(() => {
                    this.setFirstQuestion();  // XXX shomehow it won't react w/o deferring.
                }, 0);
            }
        });

        // Just propagate the preloading state here.
        reaction(() => this.sectionList.preloading, (preloading) => {
            this.setLoading(preloading);
        });
    }

    setAsCurrent(question) {
        this.currentQuestion = question;
    }
    setNextNavigation(navigationCode) {
        this.nextNavigation = navigationCode;
    }

    setLoading(preloading) {
        this.loading = preloading;
    }

    setAnsweringMode(mode) {
        if (!["unanswered", "all", "iDontKnow"].includes(mode)) {
            mode = "unanswered";
            alert(`wrong mode selected $(mode)`);
        }
        this.answeringMode = mode;
    }

    //
    // Strategy to find next question.
    // Key requirement: should be adequate and intuitive for the user.
    //
    findNextQuestion(fallThroughToAnswered) {
        let _fallThroughToAnswered;
        if (fallThroughToAnswered !== undefined) {
            _fallThroughToAnswered = fallThroughToAnswered;
        } else {
            _fallThroughToAnswered = false;
        }

        const sections = this.sectionList.sections;

        let continueFrom = this.currentQuestion;
        for (let s of sections) {
            for (let q of s.questions) {
                //
                // Skipping rules
                //

                // Skip until the starting question is found.
                if (continueFrom !== null) {
                    if (q.permanentId === continueFrom.permanentId) {
                        continueFrom = null;
                    }
                    continue;
                }

                // Mode-dependent logic.
                switch (this.answeringMode) {
                    case 'unanswered':
                        if (q.answer !== null) {
                            continue;
                        }
                        return q;

                    case 'all':
                        if (_fallThroughToAnswered) {
                            if (q.answer !== null) {
                                continue;
                            }
                            _fallThroughToAnswered = false;
                        }
                        // just get the next.
                        return q;

                    case 'iDontKnow':
                        if (q.answer?.answer !== 'i_dont_know') {
                            continue;
                        }
                        // gets the next i_dont_know.
                        return q;

                    default:
                        alert(`unknown mode ${this.answeringMode}`)
                }
            }
        }
        return null
    }


    startOver() {
        this.clearHistory();
        this.setAnsweringMode('all');

        let next = this.findNextQuestion();
        next?.setNavCurrent();
        this.setAsCurrent(next);
    }
    startUnanswered() {
        this.clearHistory();
        this.setAnsweringMode('unanswered');

        let next = this.findNextQuestion();
        next?.setNavCurrent();
        this.setAsCurrent(next);
    }
    startIDontKnow() {
        this.clearHistory();
        this.setAnsweringMode('iDontKnow');

        let next = this.findNextQuestion();
        next?.setNavCurrent();
        this.setAsCurrent(next);
    }

    //
    // Should be called when question contents is updated.
    // E.g. list of questions & visibility.
    //
    setupNavigation() {
        //
        // Close other sections when one is opened
        //
        for (const section of this.sectionList.sections) {
            reaction(() => section.navOpen, (navOpen) => {
                if (navOpen) {
                    for (const otherSection of this.sectionList.sections) {
                        if (otherSection !== section && otherSection.navOpen) {
                            otherSection.closeNav();
                        }
                    }
                }
            });
        }

        //
        // Move to next question on answer
        //
        for (const section of this.sectionList.sections) {
            for (const question of section.questions) {
                reaction(
                    () => question.answer,
                    (answer) => {
                        if (this.currentQuestion === null) {
                            this.setAsCurrent(null);
                            return;
                        }
                        if (answer !== null) {
                            const _moveToNext = () => {
                                this.pushHistory(this.currentQuestion);
                                let nextQuestion = this.findNextQuestion();
                                nextQuestion?.setNavCurrent();
                                this.setAsCurrent(nextQuestion);
                            }
                            if (this.currentQuestion.recalculatesVisibility) {
                                // move to next after update
                                (async () => {
                                    await this.sectionList.fetchData();
                                    _moveToNext();
                                })();
                            } else {
                                // simply open next
                                _moveToNext();
                            }
                        }
                    });
            }
        }

        //
        // Unmark navCurrent when current is marked.
        //
        for (const section of this.sectionList.sections) {
            for (const question of section.questions) {
                reaction(
                    () => question.navCurrent,
                    (navCurrent) => {
                        if (navCurrent) {
                            // unmark others
                            for (const s of this.sectionList.sections) {
                                for (const q of s.questions) {
                                    if (q !== question) {
                                        q.unsetNavCurrent();
                                    }
                                }
                            }
                        }
                    });
            }
        }

        //
        // Open nav section when question becomes current.
        //
        reaction(() => this.currentQuestion, (question) => {
            let {section} = this.sectionList.findByQuestion(this.currentQuestion);
            if (section !== null) {
                section.openNav();
            }
        });


        //
        // Set current when a question requests to become current.
        //
        for (const section of this.sectionList.sections) {
            for (const question of section.questions) {
                reaction(
                    () => question.iWantToBecomeCurrent,
                    (counter) => {
                        // allow
                        this.setAsCurrent(question);
                        this.setNextNavigation(`${question.permanentId}-${counter}`);
                    });
            }
        }
    }


    //
    // Set the initial question to show, if needed.
    //
    setFirstQuestion() {
        let question = this.currentQuestion;
        if (!question) {
            question = this.findNextQuestion(true);
        }
        question?.unsetNavCurrent();  // hack to trigger event. FIXME causes flickering
        question?.setNavCurrent();
        this.setAsCurrent(question);
    }


    //
    // History
    //
    pushHistory(question) {
        // quick hack to avoid duplicaties
        if (this.historyStack.length && this.historyStack[this.historyStack.length-1] === question) {
            return;
        }
        this.historyStack.push(question);
    }
    goBack() {
        if (this.historyStack.length) {
            let q = this.historyStack.pop();
            q.setNavCurrent();
            q.setIWantToBecomeCurrent();
        }
        return null;
    }
    clearHistory() {
        this.currentQuestion = null;
        this.historyStack = [];
    }


    //
    // Statistic
    //
    get countAll() {
        let total = 0;
        for (const section of this.sectionList.sections) {
            total += section.countAll;
        }
        return total;
    }
    get countVisible() {
        let total = 0;
        for (const section of this.sectionList.sections) {
            total += section.countVisible;
        }
        return total;
    }
    get countAnswered() {
        let total = 0;
        for (const section of this.sectionList.sections) {
            total += section.countAnswered;
        }
        return total;
    }
    get countIDontKnow() {
        let total = 0;
        for (const section of this.sectionList.sections) {
            total += section.countIDontKnow;
        }
        return total;
    }
}
